import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const ThemeContext = createContext();

export const THEMES = {
  DARK: 'dark',
  BLUE: 'blue',
  LIGHT: 'light',
};

const VALID = ['dark', 'blue', 'light'];
const CHOICE_KEY = 'theme_choice';

// Saves a theme the user picked, so it outlives the dark default.
const rememberChoice = (t) => {
  try { localStorage.setItem(CHOICE_KEY, t); } catch (e) { /* ignore */ }
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Dark is the default. A different theme is used only when the user picked
  // it themselves, which is stored under CHOICE_KEY. The old 'theme' key was
  // written on every visit, so a saved 'light' there may never have been a
  // real choice and is ignored. 'blue' was never a default, so it was chosen.
  const [theme, setThemeState] = useState(() => {
    try {
      const chosen = localStorage.getItem(CHOICE_KEY);
      if (VALID.includes(chosen)) return chosen;
      if (localStorage.getItem('theme') === 'blue') {
        localStorage.setItem(CHOICE_KEY, 'blue');
        return 'blue';
      }
    } catch (e) { /* storage blocked: fall back to the default */ }
    return THEMES.DARK;
  });

  // Backward compatibility — both 'dark' and 'blue' are dark modes
  const isDark = theme !== 'light';

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    // Set data-theme attribute for CSS variable selection
    root.setAttribute('data-theme', theme);

    // Set class-based theme for backward compat with isDark ternaries
    root.classList.remove('dark', 'light');
    body.classList.remove('dark-mode', 'light-mode');

    if (theme === 'light') {
      root.classList.add('light');
      body.classList.add('light-mode');
    } else {
      root.classList.add('dark');
      body.classList.add('dark-mode');
    }

    try { localStorage.setItem('theme', theme); } catch (e) { /* ignore */ }
  }, [theme]);

  // Every one of these is a user action, so each records the pick.
  const choose = useCallback((t) => {
    rememberChoice(t);
    setThemeState(t);
  }, []);

  const setTheme = useCallback((t) => {
    if (VALID.includes(t)) choose(t);
  }, [choose]);

  const toggleTheme = useCallback(
    () => choose(theme === 'light' ? 'dark' : 'light'),
    [choose, theme],
  );
  const setDarkMode = useCallback(() => choose('dark'), [choose]);
  const setLightMode = useCallback(() => choose('light'), [choose]);

  const value = useMemo(() => ({ theme, setTheme, isDark, toggleTheme, setDarkMode, setLightMode }), [theme, isDark, setTheme, toggleTheme, setDarkMode, setLightMode]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
